/**
 * StatusBadge (U5): glowing status chip. Knows the product's status vocab
 * (estimates, proposals, seam, compliance verification labels) and maps each
 * to a tone; unknown statuses render neutral. Tone can be forced via prop.
 */
import type { JSX } from 'react';
import { cx } from './cx';

export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent';

const STATUS_TONES: Record<string, BadgeTone> = {
  // estimates / versions
  draft: 'neutral',
  sent: 'info',
  viewed: 'accent',
  accepted: 'success',
  declined: 'danger',
  expired: 'warning',
  superseded: 'neutral',
  // seam: packages / grants / quotes
  open: 'info',
  closed: 'neutral',
  awarded: 'success',
  cancelled: 'danger',
  invited: 'warning',
  active: 'success',
  revoked: 'danger',
  submitted: 'info',
  revised: 'info',
  withdrawn: 'neutral',
  // compliance verification labels (always shown, never asserted as advice)
  verified: 'success',
  unverified_draft: 'warning',
  federal_anchor: 'info',
  // projects: project status / assignment status / work-item status
  planned: 'neutral',
  on_hold: 'warning',
  complete: 'success',
  assigned: 'neutral',
  in_progress: 'info',
  removed: 'danger',
  todo: 'neutral',
  blocked: 'danger',
  done: 'success',
  // misc
  pending: 'warning',
  signed: 'success',
};

export interface StatusBadgeProps {
  status: string;
  /** Override the derived tone. */
  tone?: BadgeTone;
  /** Override the derived label (default: status, underscores → spaces). */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
}

function defaultLabel(status: string): string {
  const words = status.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function StatusBadge({
  status,
  tone,
  label,
  size = 'md',
  className,
}: StatusBadgeProps): JSX.Element {
  const resolvedTone = tone ?? STATUS_TONES[status] ?? 'neutral';
  return (
    <span
      className={cx('bl-badge', `bl-badge--${resolvedTone}`, size === 'sm' && 'bl-badge--sm', className)}
    >
      <span className="bl-badge__dot" aria-hidden="true" />
      {label ?? defaultLabel(status)}
    </span>
  );
}
